import type { GameEvent } from './actions.js';
import type { MatchState, Seat, Player, Tile, CoordKey, SeriesTally } from './types.js';

/**
 * Wire format. `Map` does not survive JSON, and a seat's rack must be redacted
 * per-recipient, so serialisation is explicit rather than a blanket
 * `JSON.stringify(state)`.
 */
export interface WireSeat {
  id: string; name: string; colour: string; members: string[];
  rack: Tile[] | null;      // null when hidden from this recipient
  rackCount: number;
  grid: Array<[CoordKey, Tile]> | null;
  peeksRemaining: number; lockedUntil: number; finishedAt: number | null;
  connectedMembers: number;
  /** Visible to everybody: the table needs to see who is waiting on whom. */
  stuck: boolean;
}

export interface WireState {
  matchId: string;
  phase: MatchState['phase'];
  config: MatchState['config'];
  seats: WireSeat[];
  players: Player[];
  bagCount: number;
  leftoverCount: number;
  roundsTaken: number;
  roundsLeft: number;
  /** Letters this seat is still owed over the rest of the match. */
  tilesToCome: number;
  /** How many seats have said they are stuck, and how many it would take. */
  stuckCount: number;
  stuckNeeded: number;
  /** Epoch ms of the next forced draw, or null when that is switched off. */
  forcedDrawAt: number | null;
  startedAt: number | null;
  finishedAt: number | null;
  winnerSeatId: string | null;
  /** The seat that used every letter and ended the match — not always the winner. */
  wentOutSeatId: string | null;
  log: MatchState['log'];
  version: number;
  /** The seat this payload was built for; its rack is always included. */
  youSeatId: string | null;
  /** Totals across every match played at this table. Public. */
  series: SeriesTally;
  /** The recipient's own public player id, which it cannot work out itself. */
  youPlayerId: string | null;
}

export interface RedactOptions {
  /** Seat the recipient occupies, if any. */
  forSeatId: string | null;
  /** The recipient's public player id. */
  forPlayerId?: string | null;
  /** Seat ids whose grids the recipient is explicitly allowed to watch. */
  grantedGrids?: ReadonlySet<string>;
}

/**
 * Build the view of the match for one recipient.
 *
 * Hiding happens here, on the server, rather than in the client's rendering —
 * so "hidden" racks are genuinely absent from the payload and cannot be read
 * out of the network tab.
 */
export function redact(s: MatchState, opts: RedactOptions): WireState {
  const vis = s.config.visibility;
  const finished = s.phase === 'finished';
  const seats: WireSeat[] = [...s.seats.values()].map((seat) => {
    const mine = seat.id === opts.forSeatId;
    const revealed = finished && vis.revealOnFinish;
    const gridVisible = mine || revealed ||
      vis.grids === 'always' ||
      (vis.grids === 'onRequest' && (opts.grantedGrids?.has(seat.id) ?? false)) ||
      (vis.grids === 'afterFinish' && seat.finishedAt !== null);
    const rackVisible = mine || revealed || vis.rackLetters;
    return {
      id: seat.id, name: seat.name, colour: seat.colour, members: [...seat.members],
      rack: rackVisible ? seat.rack.map(redactTile) : null,
      rackCount: vis.rackCounts || mine || revealed ? seat.rack.length : -1,
      grid: gridVisible ? [...seat.grid].map(([k, t]) => [k, redactTile(t)]) : null,
      peeksRemaining: mine ? seat.peeksRemaining : 0,
      lockedUntil: mine ? seat.lockedUntil : 0,
      finishedAt: seat.finishedAt,
      connectedMembers: seat.connectedMembers,
      stuck: seat.stuck,
    };
  });

  const active = [...s.seats.values()].filter((x) => x.finishedAt === null);
  const need = active.length * s.config.drawPerRound;
  const roundsLeft = need === 0 ? 0 : Math.floor(s.bag.length / need);

  return {
    matchId: s.matchId,
    phase: s.phase,
    /*
     * Everything except the shuffle seed. The bag is a deterministic function
     * of the seed and the seat count, and hands are dealt as consecutive
     * slices in seat order — so a seed on the wire let any player rebuild every
     * hidden rack exactly, and every draw still to come.
     */
    config: { ...s.config, seed: '' },
    seats,
    players: [...s.players.values()].map((p) => ({ ...p })),
    bagCount: s.bag.length,
    leftoverCount: s.leftover.length,
    roundsTaken: s.roundsTaken,
    roundsLeft,
    tilesToCome: roundsLeft * s.config.drawPerRound,
    stuckCount: active.filter((x) => x.stuck).length,
    stuckNeeded: active.length,
    forcedDrawAt: s.config.forceDrawMs > 0 && s.phase === 'playing'
      ? s.lastDrawAt + s.config.forceDrawMs : null,
    startedAt: s.startedAt,
    finishedAt: s.finishedAt,
    winnerSeatId: s.winnerSeatId,
    wentOutSeatId: s.wentOutSeatId ?? null,
    log: s.log.slice(-60),
    version: s.version,
    youSeatId: opts.forSeatId,
    series: s.series ?? { games: 0, totals: {} },
    youPlayerId: opts.forPlayerId ?? null,
  };
}

const redactTile = (t: Tile): Tile => ({ ...t });

/** Rebuild a live {@link Seat} from the wire, for the client's own rendering. */
export function hydrateSeat(w: WireSeat): Seat {
  return {
    id: w.id, name: w.name, colour: w.colour, members: [...w.members],
    rack: w.rack ? w.rack.map((t) => ({ ...t })) : [],
    grid: new Map(w.grid ?? []),
    peeksRemaining: w.peeksRemaining,
    lockedUntil: w.lockedUntil,
    lastPlacementAt: 0,
    finishedAt: w.finishedAt,
    connectedMembers: w.connectedMembers,
    stuck: w.stuck,
  };
}

/**
 * The events one recipient is entitled to.
 *
 * State was redacted per recipient, but events went to every socket in the
 * room as they were — so another player could read which tile went where, the
 * words it made, the tile ids in each draw and the reason a call was refused
 * (which names the bad word), whatever the table's privacy settings said.
 *
 * The rule is simple because nothing on the client needs more: anything about
 * a particular seat's tiles, words or checks goes only to that seat. What the
 * whole table needs to know — a draw, a stuck vote, the end — goes to
 * everyone. Anyone watching a grid they are allowed to see gets it from the
 * redacted state, which already applies the table's settings.
 */
export function redactEvents(
  events: readonly GameEvent[], forSeatId: string | null,
): GameEvent[] {
  return events.filter((ev) => {
    if (PUBLIC_EVENTS.has(ev.e)) return true;
    // Fails closed: an event type added later is private to its seat until
    // somebody decides otherwise, rather than broadcast by default.
    return 'seatId' in ev && ev.seatId !== null && ev.seatId === forSeatId;
  });
}

/** The events the whole table needs. Everything else belongs to one seat. */
const PUBLIC_EVENTS: ReadonlySet<GameEvent['e']> = new Set<GameEvent['e']>([
  'drawAll', 'stuckChanged', 'bagLow', 'won', 'ended', 'rematch', 'started', 'lobby',
]);
